/**
 * Parses a `YYYY-MM-DD` date string as local-time midnight.
 *
 * `new Date('YYYY-MM-DD')` is spec'd to be UTC midnight, so in US Pacific
 * (UTC-7 / -8), `new Date('2026-05-15')` lands as `2026-05-14 17:00 local`.
 * When a user enters "2026-05-15" as a promo expiry, they mean midnight in
 * THEIR timezone — otherwise the promo flips to post-APR 4-5h before the
 * configured expiry and the display reads "5/14/2026" for a date they typed
 * as "5/15".
 *
 * This helper restores user intent for date-only strings stored from
 * `<input type="date">` (which DebtOS stores as YYYY-MM-DD via
 * `Date#toISOString().slice(0, 10)`).
 *
 * Strings that already include a time component (full ISO timestamps from
 * `Date.toISOString()`, e.g. `acceptedAt`) pass through unchanged — those are
 * already absolute moments in UTC and should not be reinterpreted.
 */
export function parseLocalDate(dateStr: string): Date {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return new Date(dateStr + 'T00:00:00')
  }
  return new Date(dateStr)
}

/**
 * Parses a `YYYY-MM` month string as local-time midnight on day 1.
 *
 * Same rationale as `parseLocalDate` but for the `YYYY-MM` shape used by
 * the ledger entries' `month` field. Pre-v0.8.44 the engine + store
 * built these as `new Date(\`${month}-01\`)` which parses UTC midnight,
 * so a January planning entry's `startDate` became Dec 31 23:00 prior
 * year in US Pacific — and a Jan-1-expiring promo silently flipped to
 * post-APR on December planning rolls.
 *
 * Returns `new Date(0)` for malformed input so callers branch on falsy
 * checks at higher layers if needed.
 */
export function parseLocalMonth(monthStr: string): Date {
  const [yStr, mStr] = monthStr.split('-')
  const y = parseInt(yStr ?? '', 10)
  const m = parseInt(mStr ?? '', 10)
  if (!Number.isFinite(y) || !Number.isFinite(m)) return new Date(0)
  return new Date(y, m - 1, 1)
}
